import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { Screen } from '~/components/layout/Screen'
import { Button } from '~/components/primitives/Button'
import { InfoButton } from '~/components/primitives/InfoButton'
import { useAsyncData } from '~/hooks/useAsyncData'
import { useBlobUrl } from '~/hooks/useBlobUrl'
import {
  comparisonPair,
  groupByMonth,
  PHOTO_VIEW_LABELS,
  PHOTO_VIEWS,
  type PhotoRecord,
  type PhotoView,
  photosForView,
  previousPair,
} from '~/models/photo'
import {
  checkPhotoQuota,
  loadPhotos,
  type QuotaVerdict,
  removePhoto,
  type SavePhotoError,
  savePhoto,
} from '~/services/photoService'
import { useDatabase } from '~/storage/DatabaseContext'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { STORE } from '~/storage/schema'
import type { PhotoId } from '~/types/ids'
import { formatCalendarDate } from '~/utils/format'
import { PhotoCapture } from './PhotoCapture'
import { PhotoComparison } from './PhotoComparison'
import styles from './PhotosPage.module.css'

/**
 * Photos (Checkpoint 3, D-081).
 *
 * Three timelines — front, side and back — each with a guided capture, a
 * thumbnail history and a first-to-latest comparison.
 *
 * **The grid reads thumbnails, never stored images.** That is the whole reason
 * the second blob exists: a year of photos lists without decoding a single
 * full-size frame.
 */
export function PhotosPage() {
  const db = useDatabase()
  const [capturing, setCapturing] = useState<PhotoView | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | undefined>(undefined)

  /* The view whose capture screen is open, remembered so focus can return to
     the control that opened it.

     **A stored element reference does not work here**: opening capture unmounts
     the whole library, so the node is detached by the time it would be focused
     and `focus()` on a detached node silently does nothing. The buttons
     register themselves on every render instead, so this map always holds the
     live one. */
  const captureButtons = useRef(new Map<PhotoView, HTMLElement>())
  const previouslyCapturing = useRef<PhotoView | undefined>(undefined)

  /* Where the library was scrolled to when capture opened.

     **Focusing the button is not enough, and the test that says otherwise is
     the reason this went unnoticed.** Opening capture unmounts the whole
     library, so returning remounts it at the top; `e2e/accessibility.spec.ts`
     asserts the button regains focus and passes, because Chrome scrolls a
     focused element into view. iOS Safari does not, so a user who added a back
     photo landed at the top of the page every time. The guard asked whether
     the element received focus, not whether the user could see it. */
  const scrollBeforeCapture = useRef<number | undefined>(undefined)

  useEffect(() => {
    const leaving = previouslyCapturing.current
    if (leaving !== undefined && capturing === undefined) {
      const button = captureButtons.current.get(leaving)
      const restoreTo = scrollBeforeCapture.current

      if (restoreTo === undefined) {
        button?.focus()
      } else {
        /* Focus first so the accessibility guarantee is unchanged, then scroll,
           so the explicit restore wins over whatever the browser would do on
           its own rather than fighting it. */
        button?.focus({ preventScroll: true })
        window.scrollTo({ top: restoreTo })
      }

      scrollBeforeCapture.current = undefined
    }
    previouslyCapturing.current = capturing
  }, [capturing])

  const { state } = useAsyncData(async () => {
    const [photos, quota, phase, project] = await Promise.all([
      loadPhotos(db),
      checkPhotoQuota(),
      getActivePhase(db),
      getActiveProject(db),
    ])
    return { photos, quota, phase, project }
  }, [STORE.photos, STORE.phases, STORE.projects])

  const onCaptured = useCallback(
    async (file: Blob, source: PhotoRecord['source']) => {
      if (capturing === undefined) return
      if (state.status !== 'ready') return

      setBusy(true)
      setMessage(undefined)

      const phaseId = state.value.phase.ok ? state.value.phase.value?.id : undefined
      const projectId = state.value.project.ok ? state.value.project.value?.id : undefined

      const saved = await savePhoto(db, {
        file,
        view: capturing,
        source,
        now: new Date(),
        ...(phaseId === undefined ? {} : { phaseId }),
        ...(projectId === undefined ? {} : { projectId }),
      })

      setBusy(false)

      if (!saved.ok) {
        setMessage(saveFailureMessage(saved.error))
        return
      }

      setCapturing(undefined)
      setMessage(`${PHOTO_VIEW_LABELS[saved.value.view]} photo saved.`)
    },
    [capturing, db, state],
  )

  if (state.status === 'loading') {
    return (
      <Screen title="Photos">
        <p>Loading…</p>
      </Screen>
    )
  }

  if (state.status === 'error') {
    return (
      <Screen title="Photos">
        <p role="alert">Project Sola could not read your photos. {state.message}</p>
      </Screen>
    )
  }

  const { photos, quota } = state.value

  if (!photos.ok) {
    return (
      <Screen title="Photos">
        <p role="alert">Project Sola could not read your photos. {photos.error.message}</p>
      </Screen>
    )
  }

  const library = photos.value

  if (capturing !== undefined) {
    const previous = photosForView(library, capturing)[0]
    return (
      <Screen title="Photos">
        {message === undefined ? null : (
          <p class={styles.message} role="alert">
            {message}
          </p>
        )}
        <PhotoCapture
          view={capturing}
          ghost={previous}
          busy={busy}
          onCaptured={(file, source) => void onCaptured(file, source)}
          onCancel={() => {
            setCapturing(undefined)
            setMessage(undefined)
          }}
        />
      </Screen>
    )
  }

  return (
    <Screen title="Photos">
      <QuotaNotice verdict={quota} />

      {message === undefined ? null : (
        <p class={styles.message} role="status">
          {message}
        </p>
      )}

      {/*
        The privacy claim stays; the mechanism moved (D-104 §5).
        ────────────────────────────────────────────────────────────────────────
        **§4's entry is photo *capture's* re-encode statement**, and that is
        untouched — `PhotoCapture` still says it beside the shutter, which is
        where a user is deciding whether to take the picture. This is the library
        screen, which repeated it every visit. What is left is the claim itself,
        in one sentence, with how it is achieved one tap away.
      */}
      <div class={styles.introRow}>
        <p class={styles.intro}>
          Three views, taken the same way each time. Nothing leaves this device unless you export
          it.
        </p>
        <InfoButton topic="how photos are stored" title="How photos are stored">
          <p>
            Project Sola removes the location and camera details from every photo as it saves it.
            There is no path that stores the camera's own file — each image is re-encoded as it
            arrives, which is what drops the metadata rather than stripping it afterwards.
          </p>
          <p>
            Photos are excluded from an export unless you turn them on, and a photo you delete is
            gone from this device.
          </p>
        </InfoButton>
      </div>

      {PHOTO_VIEWS.map((view) => (
        <ViewSection
          key={view}
          view={view}
          photos={library}
          canCapture={quota.state !== 'full'}
          onCapture={() => {
            /* Recorded before the library unmounts. Restoring an approximate
               offset is right: the section the user was working in is what
               matters, not a pixel position that the saved-message banner and
               the new thumbnail have both moved anyway. */
            scrollBeforeCapture.current = window.scrollY
            setCapturing(view)
            setMessage(undefined)
          }}
          registerCaptureButton={(element) => {
            if (element === null) captureButtons.current.delete(view)
            else captureButtons.current.set(view, element)
          }}
          onDeleted={(name) => setMessage(`${name} photo deleted.`)}
        />
      ))}
    </Screen>
  )
}

/**
 * Why a save failed, in the user's words.
 *
 * A failed save is never reported as a success, and the reason is named rather
 * than collapsed into "something went wrong" — the three causes call for three
 * different actions: free some space, try a different image, or report a
 * storage fault.
 */
function saveFailureMessage(error: SavePhotoError): string {
  if (error.kind === 'noRoom') return error.message
  if (error.kind === 'pipeline') return error.error.message
  return `That photo could not be saved. ${error.error.message}`
}

function QuotaNotice({ verdict }: { readonly verdict: QuotaVerdict }) {
  if (verdict.state === 'ok' || verdict.state === 'unknown') return null

  if (verdict.state === 'full') {
    return (
      <p class={styles.quotaFull} role="alert">
        There is not enough room on this device to save another photo. Make a backup, then delete
        photos or other data you no longer need.{' '}
        <strong>Project Sola will not delete anything for you.</strong>
      </p>
    )
  }

  return (
    <p class={styles.quotaLow} role="status">
      Storage is running low — room for roughly {verdict.photosRemaining} more{' '}
      {verdict.photosRemaining === 1 ? 'photo' : 'photos'}. A backup now is worthwhile, and you can
      delete photos you no longer need.
    </p>
  )
}

interface ViewSectionProps {
  readonly view: PhotoView
  readonly photos: readonly PhotoRecord[]
  readonly canCapture: boolean
  readonly onCapture: () => void
  /** Called with the live button on every render, for focus restoration. */
  readonly registerCaptureButton: (element: HTMLButtonElement | null) => void
  readonly onDeleted: (viewLabel: string) => void
}

function ViewSection({
  view,
  photos,
  canCapture,
  onCapture,
  onDeleted,
  registerCaptureButton,
}: ViewSectionProps) {
  const [span, setSpan] = useState<'whole' | 'recent'>('whole')

  /* Which photo is open in the detail panel, if any. Only one per section: two
     full-size images decoded at once is precisely what the thumbnail blob
     exists to avoid. */
  const [selected, setSelected] = useState<PhotoId | undefined>(undefined)

  /* The same registration pattern as the capture buttons, for the same reason:
     closing the panel re-renders the tile, so a stored node would be stale.
     Registering on every render always leaves the live one here. */
  const tileButtons = useRef(new Map<PhotoId, HTMLElement>())
  const previouslySelected = useRef<PhotoId | undefined>(undefined)

  useEffect(() => {
    const leaving = previouslySelected.current
    if (leaving !== undefined && selected === undefined) {
      tileButtons.current.get(leaving)?.focus()
    }
    previouslySelected.current = selected
  }, [selected])

  const timeline = photosForView(photos, view)
  const pair = comparisonPair(photos, view)
  const recent = previousPair(photos, view)
  const months = groupByMonth(timeline)
  const label = PHOTO_VIEW_LABELS[view]

  /* `pair` is the fallback rather than an assertion: with exactly two photos
     the two spans are the same, and the choice above is not offered. */
  const shown = span === 'recent' && recent !== undefined ? recent : pair

  /* True only when the two spans genuinely differ — with exactly two photos
     they are the same pair, and offering one choice twice implies a
     distinction that is not there. */
  const spansDiffer =
    pair !== undefined && recent !== undefined && recent.earliest.id !== pair.earliest.id

  return (
    <section class={styles.section} aria-label={`${label} photos`}>
      <div class={styles.sectionHeader}>
        <h2 class={styles.sectionTitle}>{label}</h2>
        <span class={styles.count}>
          {timeline.length === 0
            ? 'None yet'
            : `${timeline.length} ${timeline.length === 1 ? 'photo' : 'photos'}`}
        </span>
      </div>

      <Button
        onClick={onCapture}
        elementRef={registerCaptureButton}
        disabled={!canCapture}
        fullWidth
        label={`Take a ${label.toLowerCase()} photo`}
      >
        Take a {label.toLowerCase()} photo
      </Button>

      {shown === undefined ? (
        timeline.length === 1 ? (
          <p class={styles.note}>
            A comparison needs a second photo — there is nothing yet to compare this one against.
          </p>
        ) : null
      ) : (
        <>
          {/* Two pairs, because they answer different questions: the whole
              project, and the last fortnight. Offered only when they differ —
              with exactly two photos they are the same pair, and presenting
              one choice twice implies a distinction that is not there. */}
          {!spansDiffer ? null : (
            <fieldset class={styles.pairChoice}>
              <legend class={styles.pairLegend}>Compare</legend>
              <label class={styles.pairOption}>
                <input
                  type="radio"
                  name={`pair-${view}`}
                  checked={span === 'whole'}
                  onChange={() => setSpan('whole')}
                />
                <span>First and latest</span>
              </label>
              <label class={styles.pairOption}>
                <input
                  type="radio"
                  name={`pair-${view}`}
                  checked={span === 'recent'}
                  onChange={() => setSpan('recent')}
                />
                <span>Previous and latest</span>
              </label>
            </fieldset>
          )}

          <PhotoComparison
            earliest={shown.earliest}
            latest={shown.latest}
            label={label}
            pairDescription={
              span === 'whole'
                ? `First and most recent ${label.toLowerCase()} photo.`
                : `The last two ${label.toLowerCase()} photos.`
            }
          />
        </>
      )}

      {months.length === 0 ? null : (
        <div class={styles.contactSheet}>
          {/* The month sections are deliberately unnamed. An `aria-label` would
              make each one a landmark region, so three views across a year
              would put dozens of them in a screen reader's landmark list. The
              headings give the structure without the noise. */}
          {months.map((group) => {
            const open = group.photos.find((photo) => photo.id === selected)

            return (
              <section key={group.month}>
                <h3 class={styles.monthHeading}>{monthLabel(group.month)}</h3>
                <ul class={styles.grid}>
                  {group.photos.map((photo) => (
                    <Thumbnail
                      key={photo.id}
                      photo={photo}
                      label={label}
                      selected={photo.id === selected}
                      onSelect={() => setSelected(photo.id === selected ? undefined : photo.id)}
                      registerButton={(element) => {
                        if (element === null) tileButtons.current.delete(photo.id)
                        else tileButtons.current.set(photo.id, element)
                      }}
                    />
                  ))}
                </ul>

                {/* Beneath the month it belongs to, so the photo appears next
                    to the tile that opened it rather than at the end of a
                    year. Inline rather than a modal: no focus trap, no scroll
                    lock, nothing new to get wrong. */}
                {open === undefined ? null : (
                  <PhotoDetail
                    photo={open}
                    label={label}
                    onClose={() => setSelected(undefined)}
                    onDeleted={(name) => {
                      setSelected(undefined)
                      onDeleted(name)
                    }}
                  />
                )}
              </section>
            )
          })}
        </div>
      )}
    </section>
  )
}

/**
 * One tile in the contact sheet: the thumbnail and its date, and nothing else.
 *
 * **Delete used to live here**, which put a 44 px target under every tile and
 * made a twelve-photo sheet three times taller than the photos in it. It moved
 * into the detail panel, where there is one of it rather than one per photo.
 * Its wording and both of its accessible names are unchanged.
 */
function Thumbnail({
  photo,
  label,
  selected,
  onSelect,
  registerButton,
}: {
  readonly photo: PhotoRecord
  readonly label: string
  readonly selected: boolean
  readonly onSelect: () => void
  /** Called with the live button on every render, for focus restoration. */
  readonly registerButton: (element: HTMLButtonElement | null) => void
}) {
  const url = useBlobUrl(photo.thumbnailBlobId)
  const dateText = formatCalendarDate(photo.capturedDate)

  /* Short on the tile, full in the accessible name. "Saturday, August 8" wrapped
     onto two lines under a 96 px tile and ate most of the height the smaller
     tile had just saved — and the month heading directly above already says
     which month it is. A screen reader still hears the whole date, because
     there the surrounding heading is not necessarily in earshot.

     Found by taking a screenshot and looking at it. Every test passed. */
  const shortDate = formatCalendarDate(photo.capturedDate, { day: 'numeric', month: 'short' })

  return (
    <li class={styles.gridItem}>
      <button
        type="button"
        ref={registerButton}
        onClick={onSelect}
        /* The state is carried by `aria-expanded` and by the panel itself
           appearing, not by the accent border alone — a highlight nobody can
           see is state communicated by colour. */
        aria-expanded={selected}
        class={`${styles.gridButton} ${selected ? styles.gridButtonSelected : ''}`}
        aria-label={`View the ${label.toLowerCase()} photo from ${dateText}`}
      >
        {url === undefined ? (
          <div class={styles.gridImageMissing} aria-hidden="true" />
        ) : (
          <img src={url} alt="" class={styles.gridImage} />
        )}
        <span class={styles.gridDate}>{shortDate}</span>
      </button>
    </li>
  )
}

/**
 * The chosen photo, shown at a size worth looking at, with the delete control.
 *
 * Reads `blobId` rather than `thumbnailBlobId` — the stored image, not the
 * ~320 px derivative. One at a time is exactly the case the two-blob split was
 * designed for, and `useBlobUrl` revokes on unmount, so closing the panel
 * releases it.
 *
 * **A stored photo is still never edited.** This is a way to look at one. There
 * is no crop, no rotate and no retake-in-place: rotation is already applied
 * from EXIF at capture, and cropping would change what a comparison shows.
 */
function PhotoDetail({
  photo,
  label,
  onClose,
  onDeleted,
}: {
  readonly photo: PhotoRecord
  readonly label: string
  readonly onClose: () => void
  readonly onDeleted: (viewLabel: string) => void
}) {
  const db = useDatabase()
  const url = useBlobUrl(photo.blobId)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  const dateText = formatCalendarDate(photo.capturedDate)

  return (
    <div class={styles.detail}>
      {url === undefined ? (
        <div class={styles.detailImage} aria-hidden="true" />
      ) : (
        <img src={url} alt={`${label} view, ${dateText}`} class={styles.detailImage} />
      )}
      <span class={styles.detailDate}>{dateText}</span>

      {confirming ? (
        <div class={styles.confirm}>
          {/* No "are you sure?" — the copy states the consequence, as the full
              reset does (D-064). There is no undo for a deleted photo. */}
          <p class={styles.confirmText}>Deleted for good. There is no undo.</p>
          <Button
            onClick={() => {
              setBusy(true)
              void removePhoto(db, photo.id).then((result) => {
                setBusy(false)
                setConfirming(false)
                if (result.ok) onDeleted(label)
              })
            }}
            busy={busy}
            busyLabel="Deleting…"
            /* Distinct from the button that armed it. Two controls sharing one
               accessible name leaves a screen-reader user unable to tell the
               question from the answer — they hear "Delete the front photo from
               1 August" twice and cannot know which one commits. */
            label={`Confirm deleting the ${label.toLowerCase()} photo from ${dateText}`}
          >
            Delete
          </Button>
          <Button onClick={() => setConfirming(false)} variant="secondary" disabled={busy}>
            Keep
          </Button>
        </div>
      ) : (
        <div class={styles.confirm}>
          <Button
            onClick={() => setConfirming(true)}
            variant="secondary"
            label={`Delete the ${label.toLowerCase()} photo from ${dateText}`}
          >
            Delete
          </Button>
          <Button
            onClick={onClose}
            variant="secondary"
            label={`Close the ${label.toLowerCase()} photo from ${dateText}`}
          >
            Close
          </Button>
        </div>
      )}
    </div>
  )
}

/** `2026-08` as "August 2026". Parsed at UTC noon so no timezone shifts it. */
function monthLabel(month: string): string {
  const [year, index] = month.split('-')
  const date = new Date(Date.UTC(Number(year), Number(index) - 1, 15))
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}
